import Config from '../../../config';
import { readStandaloneDisplayModeFromWindow } from './isStandaloneDisplayMode';
import { getPwaIconUrl, PWA_ICON_192 } from '../constants';

/** Same prod CDN as manifest / service-worker — do not use local /icons/. */
const PWA_BOOT_ICON_URL = getPwaIconUrl(PWA_ICON_192, Config.PWA_ICONS_BASE_URL);

/** Cover the natural home-screen open window (~1–2s), without delaying after the app is already visible. */
const PWA_BOOT_SPLASH_MIN_MS = 1400;

const SPLASH_ID = 'pwa-boot-splash';

let splashStartedAt = 0;

/**
 * Show branded splash as early as possible when opening installed PWA.
 * Sits on top of #root until dismissPwaBootSplash() — covers the OS→app gap.
 */
export function mountPwaBootSplash(win: Window = window): void {
    if (!readStandaloneDisplayModeFromWindow(win)) {
        return;
    }
    if (win.document.getElementById(SPLASH_ID)) {
        return;
    }

    splashStartedAt = Date.now();

    const el = win.document.createElement('div');
    el.id = SPLASH_ID;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-label', 'Laden');
    el.innerHTML = `
      <div class="pwa-boot-splash__brand" style="color:${Config.GLOBAL_PRIMARY_COLOR}">
        <img class="pwa-boot-splash__logo" src="${PWA_BOOT_ICON_URL}" alt="" width="72" height="72" />
        <span class="pwa-boot-splash__name">${Config.GLOBAL_SITE_NAME}</span>
      </div>
    `;

    // Inline critical styles so splash works before CSS bundles load.
    el.style.cssText =
        'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;background:#e8e8e8;';
    win.document.body.appendChild(el);

    const brand = el.querySelector('.pwa-boot-splash__brand') as HTMLElement | null;
    if (brand) {
        brand.style.cssText =
            'display:inline-flex;flex-direction:column;align-items:center;gap:14px;opacity:0.85;';
    }
    const logo = el.querySelector('.pwa-boot-splash__logo') as HTMLElement | null;
    if (logo) {
        logo.style.cssText = 'display:block;border-radius:16px;object-fit:cover;';
    }
    const name = el.querySelector('.pwa-boot-splash__name') as HTMLElement | null;
    if (name) {
        name.style.cssText =
            "font-family:Nunito,system-ui,sans-serif;font-size:1.65rem;font-weight:700;letter-spacing:-0.02em;line-height:1;";
    }
}

/**
 * Remove splash once the app is ready, but not before the min open-window duration.
 */
export function dismissPwaBootSplash(win: Window = window): void {
    const el = win.document.getElementById(SPLASH_ID);
    if (!el) {
        return;
    }

    const elapsed = Date.now() - splashStartedAt;
    const wait = Math.max(0, PWA_BOOT_SPLASH_MIN_MS - elapsed);

    win.setTimeout(() => {
        el.remove();
    }, wait);
}
